import { useEffect, useState } from 'react'
import {
  getAiBrief,
  getDimensionMetrics,
  getLeaderMetrics,
  getMetricsOverview,
  getReviewMetrics,
} from '../services/MetricsDashboardService'
import {
  AiBrief,
  DimensionMetrics,
  LeaderMetrics,
  MetricsOverview,
  ReviewMetrics,
  TimeRange,
  timeRangeToParams,
} from '../types/metrics'

const TIMEOUT_CODE = 'ECONNABORTED'

type QueryState<T> = {
  data: T | null
  loading: boolean
  error: string | null
}

type UseDashboardQueries = {
  overview: QueryState<MetricsOverview>
  reviews: QueryState<ReviewMetrics>
  dimensions: QueryState<DimensionMetrics>
  leaders: QueryState<LeaderMetrics[]>
  brief: QueryState<AiBrief>
}

const initial = <T>(): QueryState<T> => ({ data: null, loading: true, error: null })

const errorState = <T>(message: string): QueryState<T> => ({ data: null, loading: false, error: message })

const successState = <T>(data: T): QueryState<T> => ({ data, loading: false, error: null })

const loadingState = <T>(): QueryState<T> => ({ data: null, loading: true, error: null })

const timeoutOr = (err: unknown, timeoutMessage: string, fallback: string): string =>
  (err as { code?: string })?.code === TIMEOUT_CODE ? timeoutMessage : fallback

const useDashboardQueries = (
  timeRange: TimeRange,
  customAfter: string,
  customBefore: string,
  refreshKey: number
): UseDashboardQueries => {
  const [overview, setOverview] = useState<QueryState<MetricsOverview>>(initial)
  const [reviews, setReviews] = useState<QueryState<ReviewMetrics>>(initial)
  const [dimensions, setDimensions] = useState<QueryState<DimensionMetrics>>(initial)
  const [leaders, setLeaders] = useState<QueryState<LeaderMetrics[]>>(initial)
  const [brief, setBrief] = useState<QueryState<AiBrief>>(initial)

  useEffect(() => {
    setOverview(loadingState)
    getMetricsOverview()
      .then(data => setOverview(successState(data)))
      .catch(() => setOverview(errorState('Could not load platform overview. Please refresh.')))
  }, [refreshKey])

  useEffect(() => {
    setDimensions(loadingState)
    getDimensionMetrics()
      .then(data => setDimensions(successState(data)))
      .catch(() => setDimensions(errorState('Could not load dimension averages.')))
  }, [refreshKey])

  useEffect(() => {
    setLeaders(loadingState)
    getLeaderMetrics()
      .then(data => setLeaders(successState(data)))
      .catch(() => setLeaders(errorState('Could not load leaders data.')))
  }, [refreshKey])

  useEffect(() => {
    const params = timeRangeToParams(timeRange, customAfter, customBefore)
    if (!params) {
      setReviews({ data: null, loading: false, error: null })
      return
    }
    setReviews(loadingState)
    getReviewMetrics(params.after, params.before)
      .then(data => setReviews(successState(data)))
      .catch(err =>
        setReviews(
          errorState(
            timeoutOr(
              err,
              'Review metrics request timed out. Try a shorter time range.',
              'Could not load review metrics for this period.'
            )
          )
        )
      )
  }, [timeRange, customAfter, customBefore, refreshKey])

  useEffect(() => {
    const params = timeRangeToParams(timeRange, customAfter, customBefore)
    if (!params) {
      setBrief({ data: null, loading: false, error: null })
      return
    }
    setBrief(loadingState)
    getAiBrief(params.after, params.before)
      .then(data => setBrief(successState(data)))
      .catch(err =>
        setBrief(
          errorState(
            timeoutOr(
              err,
              'Summary generation timed out. Try a shorter time range or refresh.',
              'Could not load cohort summary.'
            )
          )
        )
      )
  }, [timeRange, customAfter, customBefore, refreshKey])

  return { overview, reviews, dimensions, leaders, brief }
}

export default useDashboardQueries
